import { useId, useState } from 'react'

import classNames from 'classnames'

import { Icon } from '@/Atomic/Atoms/Icon'

import './TimelineSection.scss'

export interface TimelineSectionProps {
  /** Group header label, e.g. "Today", "Yesterday", "Earlier this month" or "June 2026". */
  title: string
  className?: string
  children?: React.ReactNode
}

/**
 * Collapsible container for a single group of the activity timeline.
 *
 * Renders a header row — chevron plus `title` — that toggles the visibility of
 * `children`. Purely presentational: it neither reads data nor decides which
 * group an item belongs to. Children stay mounted while collapsed so that
 * Scrivito `connect()`-wrapped items keep their subscriptions alive.
 */
export const TimelineSection: React.FC<TimelineSectionProps> = ({
  title,
  className,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const contentId = useId()
  const titleId = `${contentId}-title`

  const toggle = () => {
    setIsExpanded((prev) => !prev)
  }

  return (
    <section
      className={classNames('timeline-section', className, {
        'is-collapsed': !isExpanded,
      })}
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="timeline-section__header"
        aria-controls={contentId}
        aria-expanded={isExpanded}
        onClick={toggle}
      >
        <span className="timeline-section__chevron">
          <Icon icon="chevron-down" size="md" color="strong" />
        </span>
        <span id={titleId} className="timeline-section__title">
          {title}
        </span>
      </button>
      <div
        className="timeline-section__content"
        id={contentId}
        aria-hidden={!isExpanded ? true : undefined}
        inert={!isExpanded}
      >
        <div className="timeline-section__content-inner">{children}</div>
      </div>
    </section>
  )
}
